import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ogclTransferService from '../../services/ogclTransferService.js';
import { listIdLinkMobileClass } from '../common/uiClasses.js';
import ItclActionNoteModal from './ItclActionNoteModal.js';
import ItclShipmentActionsMenu, { itclMenuFlags } from './ItclShipmentActionsMenu.js';
import { UNLOAD_STATUS_TABS, matchesUnloadStatus } from './itclUnloadStatus.js';

/**
 * Unload tab: GCs on approved incoming ITCL requests.
 * Default list is pending unload. After unload the GC moves to the Unloaded list.
 * 3-dot: Unload before unload, Revert after unload, Remove from request.
 */
const matchesSearch = (row, query) => {
  if (!query) return true;
  const hay = [
    row.shipment_id,
    row.consignment_no,
    row.sender_name,
    row.receiver_name,
    row.request_id,
    row.from_hub_name,
    row.to_hub_name,
  ].join(' ').toLowerCase();
  return hay.includes(query.toLowerCase());
};

const asList = (response) => {
  const data = response?.data?.data || response?.data || [];
  return Array.isArray(data) ? data : [];
};

const blockedActionMessage = (action, row) => {
  if (row?.lock_reason) return row.lock_reason;
  if (action === 'unload') {
    if (row?.on_ml_sheet) {
      return 'Unload is not available after an ML sheet is created';
    }
    if (row?.unloaded) return 'Already unloaded. Use Revert to put it back to pending unload.';
    return 'Unload is not available';
  }
  if (action === 'revert') {
    if (!row?.unloaded) return 'Revert is available only after the shipment is unloaded';
    return 'Revert is not available';
  }
  return 'This shipment cannot be removed from the ITCL request';
};

const UnloadItclShipmentPanel = ({ onCountChange }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || user?.role_name;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [actingId, setActingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [unloadStatus, setUnloadStatus] = useState('pending');
  const [noteModal, setNoteModal] = useState({
    open: false, action: 'unload', items: [],
  });
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const companyReviewer = ['System Admin', 'Admin', 'Accountant', 'Hub Admin'].includes(role);
      const listParams = { status: 'APPROVED' };
      if (!companyReviewer) listParams.incoming = true;
      const [incomingRes, requestRes] = await Promise.all([
        ogclTransferService.incomingShipments(
          debouncedSearch ? { search: debouncedSearch } : {},
        ),
        ogclTransferService.list(listParams),
      ]);
      const incoming = asList(incomingRes);
      const requests = asList(requestRes);
      const details = await Promise.all(
        requests.map((row) => (
          ogclTransferService.get(row.request_id).catch(() => null)
        )),
      );
      const fromRequests = [];
      details.forEach((res, idx) => {
        const detail = res?.data?.data || res?.data || {};
        const requestId = detail.request_id || requests[idx]?.request_id;
        (detail.shipments || []).forEach((shipment) => {
          if (shipment.reverted) return;
          fromRequests.push({
            ...shipment,
            request_id: requestId,
            from_hub_name: detail.from_hub_name,
            to_hub_name: detail.to_hub_name,
            booked_by: detail.requested_by_display || detail.requested_by_name,
          });
        });
      });
      const byId = new Map();
      [...incoming, ...fromRequests].forEach((row) => {
        if (!row?.shipment_id) return;
        byId.set(String(row.shipment_id), row);
      });
      let data = Array.from(byId.values());
      if (debouncedSearch) {
        data = data.filter((row) => matchesSearch(row, debouncedSearch));
      }
      setRows(data);
      const pendingCount = data.filter((row) => !row.unloaded).length;
      const unloadedCount = data.filter((row) => row.unloaded).length;
      onCountChangeRef.current?.({ pending: pendingCount, unloaded: unloadedCount });
      setSelectedIds((prev) => prev.filter((id) => (
        data.some((row) => (
          String(row.shipment_id) === String(id) && itclMenuFlags(row, role).canUnload
        ))
      )));
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to load unload shipments');
      setRows([]);
      onCountChangeRef.current?.({ pending: 0, unloaded: 0 });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, role]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const statusCounts = useMemo(() => ({
    pending: rows.filter((row) => !row.unloaded).length,
    unloaded: rows.filter((row) => row.unloaded).length,
  }), [rows]);

  const visibleRows = useMemo(
    () => rows.filter((row) => matchesUnloadStatus(row, unloadStatus)),
    [rows, unloadStatus],
  );

  const unloadableRows = useMemo(
    () => visibleRows.filter((row) => itclMenuFlags(row, role).canUnload),
    [visibleRows, role],
  );

  const showUnloadActions = unloadStatus === 'pending';

  const selectUnloadStatus = (id) => {
    setUnloadStatus(id);
    setSelectedIds([]);
  };

  const allSelected =
    unloadableRows.length > 0 &&
    unloadableRows.every((row) => selectedIds.some((id) => String(id) === String(row.shipment_id)));

  const toggleOne = (row) => {
    if (!itclMenuFlags(row, role).canUnload) return;
    setSelectedIds((prev) => {
      const exists = prev.some((id) => String(id) === String(row.shipment_id));
      return exists
        ? prev.filter((id) => String(id) !== String(row.shipment_id))
        : [...prev, row.shipment_id];
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(unloadableRows.map((row) => row.shipment_id));
  };

  const selectedItems = () => {
    const items = unloadableRows
      .filter((row) => selectedIds.some((id) => String(id) === String(row.shipment_id)))
      .map((row) => ({
        shipment_id: row.shipment_id,
        request_id: row.request_id || row.ogcl_transfer_request_id,
      }));
    if (!items.length) {
      Alert.error('Select at least one shipment to unload');
      return null;
    }
    if (items.some((item) => !item.request_id)) {
      Alert.error('Some selected shipments are missing a transfer request id');
      return null;
    }
    return items;
  };

  const openBulkUnload = () => {
    const items = selectedItems();
    if (!items) return;
    setNoteModal({ open: true, action: 'unload', items });
  };

  const openRowAction = (action, row) => {
    const requestId = row.request_id || row.ogcl_transfer_request_id;
    if (!row.shipment_id || !requestId) {
      Alert.error('This shipment is missing a transfer request id');
      return;
    }
    const flags = itclMenuFlags(row, role);
    const allowed = (
      (action === 'unload' && flags.canUnload)
      || (action === 'revert' && flags.canRevert)
      || (action === 'remove' && flags.canRemove)
      || action === 'note'
    );
    if (!allowed) {
      Alert.error(blockedActionMessage(action, row));
      return;
    }
    setNoteModal({
      open: true,
      action,
      items: [{ shipment_id: row.shipment_id, request_id: requestId }],
    });
  };

  const runAction = async (actionNote) => {
    const { action, items } = noteModal;
    if (!items?.length) return;
    const trimmed = String(actionNote || '').trim();
    if (action !== 'revert' && !trimmed) {
      Alert.error('Enter a note before continuing');
      return;
    }
    const firstId = items[0].shipment_id;
    try {
      setActingId(items.length === 1 ? firstId : 'bulk');
      if (action === 'revert') {
        const item = items[0];
        await ogclTransferService.revert(item.request_id, {
          shipment_ids: [item.shipment_id],
          note: trimmed,
        });
        Alert.success(`${item.shipment_id} reverted to pending unload`);
      } else if (action === 'note') {
        const item = items[0];
        await ogclTransferService.note(item.request_id, {
          shipment_ids: [item.shipment_id],
          note: trimmed,
        });
        Alert.success(`Note saved for ${item.shipment_id}`);
      } else if (action === 'remove') {
        const item = items[0];
        await ogclTransferService.remove(item.request_id, {
          shipment_ids: [item.shipment_id],
          note: trimmed,
        });
        Alert.success(`${item.shipment_id} removed from the ITCL request`);
      } else if (items.length === 1) {
        const item = items[0];
        await ogclTransferService.unload(item.request_id, {
          shipment_ids: [item.shipment_id],
          note: trimmed,
        });
        Alert.success(`${item.shipment_id} marked unloaded`);
      } else {
        const response = await ogclTransferService.unloadBulk({ items, note: trimmed });
        const errors = response.data?.data?.errors || [];
        if (errors.length) {
          Alert.error(errors[0]?.error || 'Some shipments could not be unloaded');
        } else {
          Alert.success(`Unloaded ${items.length} shipment(s)`);
        }
      }
      setSelectedIds([]);
      setNoteModal({ open: false, action: 'unload', items: [] });
      await fetchRows();
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to complete the action');
    } finally {
      setActingId(null);
    }
  };

  const statusBadge = (row) => {
    if (row.unloaded) {
      return { label: 'Unloaded', className: 'bg-green-50 text-green-700 border-green-200' };
    }
    return { label: 'Pending unload', className: 'bg-amber-50 text-amber-800 border-amber-200' };
  };

  const busy = actingId != null;

  const renderRowActions = (row) => (
    <ItclShipmentActionsMenu
      row={row}
      role={role}
      menuKey={row.shipment_id}
      openMenuId={openMenuId}
      disabled={busy}
      onToggle={() => setOpenMenuId(openMenuId === row.shipment_id ? null : row.shipment_id)}
      onClose={() => setOpenMenuId(null)}
      onUnload={() => openRowAction('unload', row)}
      onNote={() => openRowAction('note', row)}
      onRevert={() => openRowAction('revert', row)}
      onRemove={() => openRowAction('remove', row)}
    />
  );

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4">
        <div className="flex flex-row flex-wrap items-end gap-3">
          <div className="shrink-0">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <div className="flex gap-1.5 p-1 rounded-xl bg-gray-50 border border-gray-200">
              {UNLOAD_STATUS_TABS.map((tab) => {
                const active = unloadStatus === tab.id;
                const count = statusCounts[tab.id] || 0;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => selectUnloadStatus(tab.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
                      active
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/70'
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`min-w-[1.1rem] text-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                        tab.id === 'pending'
                          ? (active ? 'bg-amber-50 text-amber-800' : 'bg-amber-50/80 text-amber-700')
                          : (active ? 'bg-green-50 text-green-700' : 'bg-green-50/80 text-green-700')
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex-1 min-w-[14rem]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="GC number, sender, receiver…"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="secondary" type="button" onClick={fetchRows} disabled={loading || busy}>
              Refresh
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={openBulkUnload}
              loading={actingId === 'bulk'}
              disabled={!showUnloadActions || busy || selectedIds.length === 0}
            >
              Unload{selectedIds.length ? ` (${selectedIds.length})` : ''}
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <Loader fullScreen={false} message="Loading unload shipments..." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {visibleRows.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">
              {unloadStatus === 'unloaded'
                ? 'No unloaded shipments'
                : 'No shipments pending unload'}
            </p>
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      {showUnloadActions ? (
                        <th className="px-4 py-2.5 text-left">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            disabled={!unloadableRows.length || busy}
                            className="w-4 h-4"
                            title="Select all to unload"
                          />
                        </th>
                      ) : null}
                      <th className="text-left px-4 py-2.5 font-semibold">GC Number</th>
                      <th className="text-left px-4 py-2.5 font-semibold">CN</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Request</th>
                      <th className="text-left px-4 py-2.5 font-semibold">From → To hub</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Booked by</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visibleRows.map((row) => {
                      const badge = statusBadge(row);
                      const checked = selectedIds.some((id) => String(id) === String(row.shipment_id));
                      return (
                        <tr key={row.shipment_id} className={row.unloaded ? 'bg-gray-50/70' : 'hover:bg-gray-50'}>
                          {showUnloadActions ? (
                            <td className="px-4 py-2.5">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!itclMenuFlags(row, role).canUnload || busy}
                                onChange={() => toggleOne(row)}
                                className="w-4 h-4"
                              />
                            </td>
                          ) : null}
                          <td className="px-4 py-2.5 font-medium text-secondary">
                            {row.shipment_id}
                            {row.action_note ? (
                              <p className="text-xs font-normal text-gray-500 mt-0.5 break-words">Note: {row.action_note}</p>
                            ) : null}
                          </td>
                          <td className="px-4 py-2.5 text-gray-700">{row.consignment_no || '—'}</td>
                          <td className="px-4 py-2.5">
                            <button
                              type="button"
                              className="text-secondary hover:underline"
                              onClick={() => navigate(`/ogcl-transfer-requests/view/${encodeURIComponent(row.request_id)}`)}
                            >
                              {row.request_id || '—'}
                            </button>
                          </td>
                          <td className="px-4 py-2.5">
                            {(row.from_hub_name || row.sender_location || '—')}
                            {' → '}
                            {(row.to_hub_name || row.receiver_location || '—')}
                          </td>
                          <td className="px-4 py-2.5">{row.booked_by || '—'}</td>
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${badge.className}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right">{renderRowActions(row)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lg:hidden divide-y divide-gray-100">
                {showUnloadActions ? (
                  <div className="px-3 py-2 flex items-center gap-2 bg-gray-50">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={!unloadableRows.length || busy}
                      className="w-4 h-4"
                    />
                    <span className="text-xs font-semibold text-gray-600">Select all to unload</span>
                  </div>
                ) : null}
                {visibleRows.map((row) => {
                  const badge = statusBadge(row);
                  const checked = selectedIds.some((id) => String(id) === String(row.shipment_id));
                  return (
                    <div key={row.shipment_id} className="p-3 flex gap-3">
                      {showUnloadActions ? (
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!itclMenuFlags(row, role).canUnload || busy}
                          onChange={() => toggleOne(row)}
                          className="w-4 h-4 mt-1"
                        />
                      ) : null}
                      <div className="min-w-0 flex-1 space-y-1">
                        <button
                          type="button"
                          className={listIdLinkMobileClass}
                          onClick={() => navigate(`/ogcl-transfer-requests/view/${encodeURIComponent(row.request_id)}`)}
                        >
                          {row.shipment_id}
                        </button>
                        <p className="text-xs text-gray-600">CN: {row.consignment_no || '—'}</p>
                        <p className="text-xs text-gray-600">
                          {(row.from_hub_name || row.sender_location || '—')}
                          {' → '}
                          {(row.to_hub_name || row.receiver_location || '—')}
                        </p>
                        <p className="text-xs text-gray-500">Booked by: {row.booked_by || '—'}</p>
                        {row.action_note ? (
                          <p className="text-xs text-gray-500 break-words">Note: {row.action_note}</p>
                        ) : null}
                        <p className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${badge.className}`}>
                          {badge.label}
                        </p>
                        <div className="pt-1 flex justify-end">{renderRowActions(row)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
      <ItclActionNoteModal
        open={noteModal.open}
        action={noteModal.action}
        shipmentCount={noteModal.items?.length || 0}
        loading={busy}
        onClose={() => !busy && setNoteModal({ open: false, action: 'unload', items: [] })}
        onConfirm={runAction}
      />
    </div>
  );
};

export default UnloadItclShipmentPanel;
